import { useMsal } from "@azure/msal-react";
import {
    ChoiceGroup,
    DatePicker, defaultDatePickerStrings,
    DayOfWeek,
    DefaultButton, DirectionalHint, IColumn, IPanelProps, IRenderFunction, MessageBar,
    MessageBarType,
    Panel,
    PanelType,
    PrimaryButton, Separator,
    Stack,
    TextField,
    PivotItem,
    IStackProps,
    useTheme,
    Text
} from "@fluentui/react";
import { RootState } from "app/store";
import { IGridItem } from "components/controls/CustomDetailList/IGridItem";
import LookupControlBase from "components/controls/LookupControlBase";
import LookupControlDialog, { TSearchPredicateAsync } from "components/controls/LookupControlDialog";
import { appStyles, createNonStandard, createNonStandardColumns, createNonStandardTemplate, createNonStandardTemplateColumns, emitCustomEvent, EViewType, fadeIn, friendlyValue, panelButtonsStyles, reconcileTiers } from "global/Application";
import { useApplication } from "global/Application/Context";
import { MsalTokenSource } from "global/DataSources/MsalTokenSource";
import PageLoading from "global/PageLoading";
import { addUpdateNonStandards, addUpdateNonStandardTemplates } from "global/Reducer/dataSlice";
import { ArrayToLookupByPredicate, ArrayToLookupByPredicateWithMap, formatDate, parseDate, timestamp } from "global/Utility/TSFunctions";
import * as IXrmTypes from "model/CRM";
import { IPanelEvent, IValueChoiceGroupOption } from "model/Extension";
import * as FAM from "model/FlexibleAttributeModel";
import { useEffect, useMemo, useState } from "react";
import { connect, ConnectedProps, useDispatch } from "react-redux";
import { ITierLevelChange } from "views/TierWizard";
import { AppDataSource } from "../../global/DataSources/AppDataSource";
import SnapshotLookup from "./Controls/SnapshotLookup";
import InformationCallout from "components/controls/InformationCallout";
import FlexibleFieldView, { EScopeType } from "./FlexibleFieldView";
import NonStandardEditor, { EVariant } from "./NonStandardEditor";
import TextFieldControl from "../../components/controls/TextFieldControl";
import { ConfirmationDialog } from "../../components/controls/ConfirmationDialog";
import NonStandardTemplateEditor from "./NonStandardTemplateEditor";
import ControlMask from "../../components/controls/ControlMask";
import ToggleControl from "../../components/controls/ToggleControl";
import { EControlType } from "../../components/ControlWrapper";
import { setProcessing } from "../../global/Reducer/statusSlice";
import saveAs from "file-saver";

interface IConfirm {
}


interface IProps extends TComponentConnectedProps {
    //TODO: unmount component with delay for animation
    isOpen: boolean;

    // cancellation of transaction/partial actions
    onCancel?: () => void;

    // dialog closure request from parent component
    onDismiss: () => void

    // use this prior to completion of primary action for verification/or to perform action on parent
    onConfirm?: (e?: IPanelEvent<IConfirm>) => void;

    // start condition
    defaultPayload?: IConfirm;

    benefit?: FAM.IBenefit;
    groupConfiguration?: FAM.IGroupConfigurationData;
    section?: IXrmTypes.bcbs_config_section;

    nonStandard?: FAM.INonStandard;

    onAdd?: () => void
}

const enum EView {
    View_CreateTemplate,
    View_LinkTemplate,
    View_Source
};

enum ENonstandardScope {
    GroupConfiguration,
    Benefit
}

enum ENonstandardTemplateScope {
    GroupConfiguration,
}


interface IScopeSearchResult extends FAM.IObjectWithNameId {
    groupConfiguration: FAM.IGroupConfigurationData | null;
    benefit: FAM.IBenefit | null;
}
interface INonstandardSearchResult extends FAM.IObjectWithNameId {
    item: IBenefitNonstandardScope | IGroupConfigurationNonstandardScope;
}

interface INonstandardTemplateSearchResult extends FAM.IObjectWithNameId {
    item: IGroupConfigurationNonstandardTemplateScope;
}

interface INonstandardScope {
    nonStandard: FAM.INonStandard;
    parent: FAM.IBenefit | FAM.IGroupConfigurationData;
    type: ENonstandardScope;
}

interface INonstandardTemplateScope {
    nonStandardTemplate: FAM.INonStandardTemplate;
    parent: FAM.IGroupConfigurationData;
    type: ENonstandardTemplateScope;
}

interface IBenefitNonstandardScope extends INonstandardScope {
    parent: FAM.IBenefit
    type: ENonstandardScope.Benefit;
}
interface IGroupConfigurationNonstandardScope extends INonstandardScope {
    parent: FAM.IGroupConfigurationData;
    type: ENonstandardScope.GroupConfiguration;
}
interface IGroupConfigurationNonstandardTemplateScope extends INonstandardTemplateScope {
    parent: FAM.IGroupConfigurationData;
    type: ENonstandardTemplateScope.GroupConfiguration;
}

interface IImportNonstandardContext {
    snapshot?: IXrmTypes.bcbs_groupconfigurationsnapshot | null;
    json?: FAM.IGroupConfigurationData | null;
    nonStandardSearchResult?: INonstandardScope[] | null;
    nonStandardTemplateSearchResult?: INonstandardTemplateScope[] | null;
    scopeSearchResult?: IScopeSearchResult[] | null;
    searching?: boolean;
}

const NonStandardTemplateWizard = (props: IProps) => {
    // current view state/stages
    const [view, setView] = useState<EView | null>(null);
    const [validationMessage, setValidationMessage] = useState<string | null>(null);

    const [linkedNSSection, setLinkedNSSection] = useState<IXrmTypes.bcbs_config_section | null>(null);
    const [linkedNSCategoryType, setLinkedNSCategoryType] = useState<IXrmTypes.bcbs_config_category | null>(null);
    const [linkedNonStandards, setLinkedNonStandards] = useState<FAM.INonStandard[] | null>(null);

    const [optionSource, setOptionSource] = useState<IValueChoiceGroupOption<EView> | null>(null);
    const [inputValue, setInputValue] = useState('');
    const [_nonStandardTemplate, setNonStandardTemplate] = useState<FAM.INonStandardTemplate | null>(null)
    const [isWizardOpen, setIsWizardOpen] = useState(false);

    const [sourceContext, setSourceContext] = useState<IImportNonstandardContext | null>({
        nonStandardSearchResult: null,
        scopeSearchResult: null,
        snapshot: null,
        json: null,
    });

    const [alertIsMounted, setAlertIsMounted] = useState(false);
    const [alertIsOpen, _setAlertIsOpen] = useState(false);
    const setAlertIsOpen: React.Dispatch<React.SetStateAction<boolean>> = (value) => {
        if (!alertIsMounted)
            setAlertIsMounted(true);
        _setAlertIsOpen(value);
    }

    const application = useApplication();

    const { instance } = useMsal();
    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);


    const disableAddingNonstandardByChangeType =
        (props.benefit && !application.ChangeTypePermissions.canAddBenefitNonstandard) ||
        (props.groupConfiguration && !application.ChangeTypePermissions.canAddGroupConfigurationNonstandard);

    const categoryNonStandardTemplate = props.state.data.config.cache.map_bcbs_config_category_named['Non-Standard Template'];

    const dispatch = useDispatch();

    useEffect(() => {
        if (props.isOpen) {
            //if we are creating from existing non standard
            if (props.nonStandard) {
                let nsClone = { ...props.nonStandard };
                setLinkedNonStandards([nsClone]);
                if (props.nonStandard.sectionRef !== null) {
                    var section = props.state.data.config.cache.map_bcbs_config_section[nsClone!.sectionRef!.id];
                    let categoryID = section!._bcbs_config_categoryid_value!;
                    setLinkedNSSection(section);
                    setLinkedNSCategoryType(props.state.data.config.cache.map_bcbs_config_category[categoryID]);
                }
                setView(EView.View_Source); // if there is a non standard selected, open up the options
            }
            else
            {
                //if there is no nonstandard selected - only open the template creation panel
                setView(EView.View_CreateTemplate);
            }
            setOptionSource(null);
            setSourceContext(q => {
                return {
                    json: null,
                    nonStandardSearchResult: null,
                    scopeSearchResult: null,
                    snapshot: null
                }
            });
            const nonStandardTemplate: FAM.INonStandardTemplate = createNonStandardTemplate(
                props.state.data,
                {
                    id: "",
                    name:`Non-StandardTemplate - New - ${timestamp()}`,
                    category: categoryNonStandardTemplate,
                    linkedCategory: null,
                    linkedCategoryName: null,
                }
            );

            if (props.nonStandard) {
                let nonStandardBenefitDefintion = props.nonStandard.fields.find(field => field.configRef.id === "ff72f3f2-d38d-ec11-b400-0022480a9a56");
                let nonStandardPayAccordingToBenefit = props.nonStandard.fields.find(field => field.configRef.id === "fd72f3f2-d38d-ec11-b400-0022480a9a56");

                let templateDefinitionField = nonStandardTemplate.fields.find(field => field.configRef.id === "1fe66c74-710c-ef11-9f89-6045bdeb64f8"); //template definition
                templateDefinitionField!.values[0].value = structuredClone(nonStandardBenefitDefintion?.values[0].value!);

                if (nonStandardPayAccordingToBenefit) {
                    let templatePayAccordingField = nonStandardTemplate.fields.find(field => field.configRef.id === "05beb613-720c-ef11-9f89-6045bdeb64f8"); //template pay according
                    templatePayAccordingField!.values[0].value = structuredClone(nonStandardPayAccordingToBenefit?.values[0].value!);
                }

                nonStandardTemplate.name = props.nonStandard.name;

                let categoryID = section!._bcbs_config_categoryid_value!;
                nonStandardTemplate.linkedCategoryTypeId = categoryID;
            }

            setNonStandardTemplate(nonStandardTemplate)
        }
    }, [props.isOpen]);

    const optionsSource: IValueChoiceGroupOption<EView>[] = [
        {
            key: 'template',
            text: 'Create A New Non-Standard Template',
            data: EView.View_CreateTemplate,
            disabled: props.state.status.viewType === EViewType.ViewOnly || disableAddingNonstandardByChangeType
        },
        {
            key: 'templateLink',
            text: 'Link Non-Standard To An Existing Template',
            data: EView.View_LinkTemplate,
            disabled: props.state.status.viewType === EViewType.ViewOnly || disableAddingNonstandardByChangeType
        },
    ];

    const handleInputChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue: string | undefined) => {
        setInputValue(newValue ?? "");
    };

    /**
   * Search for all available non-standards inside a snapshot and output list of scopes
   * Group Configuration, Benefit 1, Benefit 2, ...
   * @param value 
   * @returns 
   */
    const onSearchTemplateScope: TSearchPredicateAsync<IScopeSearchResult> = async (value) => {
        const searchValue = value.toLowerCase() ?? '';

        if (!sourceContext?.snapshot || !sourceContext.json)
            throw new Error('expecting snapshot to search');

        return new Promise((resolve, reject) => {

            let json = sourceContext.json;

            let items: IGridItem<IScopeSearchResult>[] = [];

            if (!json)
                reject(new Error('unexpected'));

            if ((json?.nonStandardTemplates?.length ?? 0) > 0) {
                let add = false;

                const search = 'group configuration'.toLowerCase();

                // if we are not searching for anything, return as/is
                if (!searchValue)
                    add = true;

                // lets look into search
                if (search.indexOf(searchValue) > -1)
                    add = true;

                if (add)
                    items.push({
                        key: '-1',
                        _index: -1,
                        scope: 'Group Configuration',
                        name: 'Group Configuration',
                        search: 'group configuration',
                        total: json?.nonStandardTemplates?.length ?? 0,
                        data: {
                            id: json!.id,
                            name: json!.name,
                            groupConfiguration: json ?? null,
                            benefit: null
                        }
                    });
            }

            resolve({
                columns: [{
                    key: 'scope',
                    minWidth: 550,
                    name: 'Entity',
                    fieldName: 'scope',
                    isResizable: true,
                },
                {
                    key: 'total',
                    minWidth: 50,
                    name: 'Count',
                    fieldName: 'total',
                    isResizable: true,
                }],
                items: items,
                showNoResultPrompt: items.length === 0,
                showLimitedResultPrompt: false,
            });
        });
    }

    const theme = useTheme();
    const columnProps: Partial<IStackProps> = useMemo(() => {
        return {
            tokens: { childrenGap: 15 },
            styles: {
                root: {
                    boxShadow: theme.effects.elevation16,
                    padding: '20px',
                    margin: '20px 0px 0px 0px',
                    backgroundColor: theme.semanticColors.menuBackground
                },
            },
        };
    }, [theme]);

    const onRenderProperties: IRenderFunction<IPanelProps> = (bodyProps, defaultRender) => {
        if (!_nonStandardTemplate)
            return <></>

        const isBulk = _nonStandardTemplate?.id === 'bulk';

        const displayAddButtons = (_nonStandardTemplate.linkedCategoryTypeId && _nonStandardTemplate.relationships && _nonStandardTemplate.relationships !== []);

        return <PivotItem
            headerText={"Properties"}
            key={"nonStandardTemplateProperties"}
        >
            <Stack {...columnProps}>
                <TextFieldControl
                    label={"Name"}
                    fieldValueOnChange={(event, value) => {
                        setNonStandardTemplate(t => {
                            return {
                                ...t!,
                                name: value.value ?? ''
                            };
                        })
                    }}
                    defaultFieldValue={{ type: 'string', value: _nonStandardTemplate.name ?? '' }}
                />

                {/*<ControlMask*/}
                {/*    label="BCBS Reference Only"*/}
                {/*    defaultLocked={isBulk}*/}
                {/*    onUnlock={() => {*/}
                {/*        setNonStandardTemplate(t => { return { ...t!, deactivated: false }; });*/}
                {/*    }}*/}
                {/*>*/}
                {/*    <ToggleControl*/}
                {/*        label={"BCBS Reference Only"}*/}
                {/*        checked={nonStandardTemplate.referenceOnly ?? false}*/}
                {/*        disabled={props.disabled}*/}
                {/*        onChange={(_, checked) => {*/}
                {/*            setNonStandardTemplate(t => { return { ...t!, referenceOnly: checked ?? false }; });*/}
                {/*        }}*/}
                {/*    />*/}
                {/*</ControlMask>*/}

                <ControlMask
                    label="Deactivate"
                    defaultLocked={isBulk}
                    onUnlock={() => {
                        setNonStandardTemplate(t => { return { ...t!, deactivated: false }; });
                    }}
                >
                    <ToggleControl
                        label={"Deactivate"}
                        checked={_nonStandardTemplate.deactivated ?? false}
                        onChange={(_, checked) => {
                            setNonStandardTemplate(t => { return { ...t!, deactivated: checked ?? false }; });
                        }}
                    />
                </ControlMask>

                {/*{displayAddButtons &&*/}
                {/*    <PrimaryButton*/}
                {/*        //THIS IS THE BUTTON TO apply more non standards to this existing template*/}
                {/*        text='Add Non-Standards'*/}
                {/*        styles={panelButtonsStyles}*/}
                {/*    //on confirm on which non standards we are applying template, update them with the template fields*/}

                {/*    />*/}
                {/*}*/}
            </Stack>
        </PivotItem>
    }

    /**
     * Renders an information callout with audit data.
     * @param auditData - The audit data to display in the callout.
     * @returns The rendered InformationCallout component.
     */
    const onRenderInformationCallout = (auditData?: IGridItem<INonstandardTemplateSearchResult>) => {

        if (!auditData?.data?.item.nonStandardTemplate)
            return <></>;

        const nonStandardTemplate = auditData?.data?.item.nonStandardTemplate;

        return <InformationCallout
            hover
            postText={auditData.compareKeys}
            directionalHint={DirectionalHint.rightCenter}
            calloutClassName={appStyles.calloutView}
        >
            <div style={{ margin: '10px' }}>
                <NonStandardTemplateEditor
                    variant={EVariant.viewer}
                    isOpen={true}
                    disabled={true}
                    defaultValue={nonStandardTemplate ?? null}
                    openViewer={() => { } }
                />

            </div>
        </InformationCallout>;
    }

    /**
    * Search for all records applicable to selected scope
    * @param value 
    * @returns 
    */
    const onSearchNonStandardTemplates: TSearchPredicateAsync<INonstandardTemplateSearchResult> = async (value) => {
        const searchValue = value?.toLowerCase() ?? '';

        return new Promise((resolve, reject) => {

            const items: IGridItem<INonstandardTemplateSearchResult>[] = [];

            sourceContext?.nonStandardTemplateSearchResult?.forEach((nonStandardTemplateItem, ix) => {

                let add = false;

                if (!add && !searchValue)
                    add = true;

                if (!add && nonStandardTemplateItem.nonStandardTemplate.name.toLowerCase().indexOf(searchValue) > -1)
                    add = true;

                if (add) {

                    const item: IGridItem<INonstandardTemplateSearchResult> = {
                        key: ix,
                        _index: ix,
                        name: nonStandardTemplateItem.nonStandardTemplate.name,
                        scope: nonStandardTemplateItem.parent.name,
                        data: {
                            name: nonStandardTemplateItem.nonStandardTemplate.name,
                            id: nonStandardTemplateItem.nonStandardTemplate.id,
                            item: nonStandardTemplateItem as IGroupConfigurationNonstandardTemplateScope,
                        }
                    };


                    nonStandardTemplateItem.nonStandardTemplate.fields.forEach(eaField => {
                        const configAttributeId = eaField.configRef.id;
                        eaField.values.forEach(eachFieldValue => {
                            item[`${configAttributeId}-${eachFieldValue?.tierRef?.id ?? ''}`] = eachFieldValue.value;
                        });
                    });

                    items.push(item);

                }
            })

            const quickViewColumn: IColumn = {
                key: 'view',
                minWidth: 50,
                name: 'View',
                fieldName: 'view',
                isResizable: true,
                onRender(item: IGridItem<INonstandardTemplateSearchResult>, index, column) {
                    return onRenderInformationCallout(item)
                },
            };

            const categoryNonStandardTemplate = props.state.data.config.cache.map_bcbs_config_category_named['Non-Standard Template'];

            // generating a temporary non-standard to match the schema
            const columns = createNonStandardTemplateColumns(
                props.state.data.config,
                {
                    category: categoryNonStandardTemplate,
                    linkedCategory: null,
                    config: props.state.data.config,
                },
                undefined,
                undefined,
                (field, configAttribute, configMasterAttribute) => {

                    // https://bcbsonline.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=ff72f3f2-d38d-ec11-b400-0022480a9a56
                    if (configAttribute?.bcbs_config_attributeid === 'ff72f3f2-d38d-ec11-b400-0022480a9a56')
                        return true;

                    return false;
                }
            );


            resolve({
                columns: [
                    {
                        key: 'name',
                        minWidth: 350,
                        name: 'Name',
                        fieldName: 'name',
                        isResizable: true,
                    },
                    {
                        key: 'scope',
                        minWidth: 300,
                        name: 'Entity',
                        fieldName: 'scope',
                        isResizable: true,
                    },
                    ...columns,
                    quickViewColumn,
                ],
                items: items,
                showNoResultPrompt: items.length === 0,
                showLimitedResultPrompt: false,

            })
        });
    }

    function onRenderLinkTemplate(): JSX.Element {

        return <div className={fadeIn}>
            {onRenderValidationDialog()}
            <Stack tokens={{ childrenGap: 10 }}>

                {sourceContext?.searching &&
                    <PageLoading />
                }

                <LookupControlBase
                    label='Entity'
                    value={sourceContext?.scopeSearchResult?.[0]?.name}
                    disabled
                    onLookup={() => {
                        setSourceContext(q => {
                            return {
                                json: null,
                                nonStandardSearchResult: null,
                                scopeSearchResult: null,
                                snapshot: null
                            }
                        });
                    }}
                />

                <LookupControlDialog
                    isOpen={sourceContext?.snapshot !== null &&
                        sourceContext?.json !== null &&
                        sourceContext?.scopeSearchResult === null
                    }
                    label={'Entity'}
                    onSearch={onSearchTemplateScope}
                    variant={"single"}
                    onPick={(picks) => {
                        const nonStandardTemplates: INonstandardTemplateScope[] = [];

                        // nothing to do
                        if (!picks || picks.length < 1) {
                            setSourceContext(q => {
                                return {
                                    json: null,
                                    nonStandardTemplateSearchResult: null,
                                    scopeSearchResult: null,
                                    snapshot: null
                                }
                            });
                            return;
                        }

                        // Group Configuration Scope:
                        // --------------------------------------
                        const gcNS = picks.find(q => sourceContext?.json === q.groupConfiguration);

                        if (gcNS && sourceContext?.json?.nonStandardTemplates)
                            sourceContext?.json?.nonStandardTemplates.forEach(t => {

                                if (t.deactivated || t.deactivated === undefined)
                                    return;
                                //show only correct categories and templates with this category as a relationship
                                let relationshipExists = false;
                                let categoryId = props.benefit?.id ?? props.groupConfiguration?.id;

                                t.relationships?.forEach(relationship => {
                                    if (relationship.categoryID === categoryId)
                                        relationshipExists = true;
                                });

                                if (t.linkedCategoryTypeId !== undefined && linkedNSCategoryType !== null) {
                                    if (t.linkedCategoryTypeId !== linkedNSCategoryType?.bcbs_config_categoryid || !relationshipExists) {
                                        //do not display template if the category type doesnt match
                                        //do not display template if we dont have this category already in the template
                                        return;
                                    }
                                }

                                nonStandardTemplates.push({
                                    type: ENonstandardTemplateScope.GroupConfiguration,
                                    parent: sourceContext.json!,
                                    nonStandardTemplate: t,
                                });
                            });

                        setSourceContext(q => {
                            return {
                                ...q,
                                nonStandardTemplateSearchResult: nonStandardTemplates,
                                scopeSearchResult: picks,
                            }
                        });

                    }}
                    onCancel={() => {

                        setSourceContext(q => {
                            return {
                                ...q,
                                snapshot: null,
                                nonStandardTemplateSearchResult: null,
                                scopeSearchResult: null,
                            }
                        });
                    }}
                    onDismiss={() => {
                        setSourceContext(q => {
                            return {
                                ...q,
                                snapshot: null,
                                nonStandardTemplateSearchResult: null,
                                scopeSearchResult: null,
                            }
                        });
                    }}

                />

                <LookupControlDialog
                    isOpen={!!sourceContext?.nonStandardTemplateSearchResult}
                    label={'Non-Standard Templates'}
                    variant="single"
                    onSearch={onSearchNonStandardTemplates}
                    onPick={(picks) => {

                        const nonStandardTemplates: FAM.INonStandardTemplate[] = [];
                        //non standard -> template side
                        //THIS IS TO CONNECT AN EXISTING NON STANDARD TO A TEMPLATE
                        picks?.forEach(pick => {
                            const fieldMap = ArrayToLookupByPredicate(pick.item.nonStandardTemplate.fields, q => q.configRef.id);
                            const linkedConfigCategory = props.state.data.config.cache.map_bcbs_config_category[pick.item.nonStandardTemplate.linkedCategoryTypeId!];

                            // lets create a new nonstandard template as
                            let template: FAM.INonStandardTemplate = createNonStandardTemplate(
                                props.state.data,
                                {
                                    id: "",
                                    name: `Non-StandardTemplate - New - ${timestamp()}`,
                                    category: categoryNonStandardTemplate,
                                    linkedCategory: linkedConfigCategory ?? linkedNSCategoryType,
                                    linkedCategoryName: pick.item.nonStandardTemplate.linkedCategoryName ?? linkedNSCategoryType?.bcbs_name!,
                                    relationships: pick.item.nonStandardTemplate.relationships ?? []
                                }
                            );

                            let tierChanges: ITierLevelChange[] = [];

                            // transfer matching properties 
                            template.name = pick.item.nonStandardTemplate.name;
                            template.id = pick.item.nonStandardTemplate.id;

                            
                            //let newNonStandardList: FAM.INonStandard[] = [];
                            let newRelationshipList = template.relationships!.map(rel => ({
                                ...rel,
                                sectionRefs: (rel.sectionRefs!.map(secRef => ({
                                    ...secRef,
                                    nonstandards: [...(secRef.nonstandards || [])] // Clone the nonstandards array
                                })) || [])
                            }));

                            if (linkedNonStandards !== null) { 
                                linkedNonStandards?.forEach(linkedNS => {
                                    //when linking new non standard, we need to check if the section id already exists on the current category
                                    //so, get current category of the ns
                                    let incomingNSCategoryId = props.benefit?.id ? props.benefit?.id : props.groupConfiguration?.id;

                                    // Find the category in the template to update
                                    let category = newRelationshipList.find(cat => cat.categoryID === incomingNSCategoryId);
                                    const clonedNS: FAM.INonStandard = { ...linkedNS, partOfTemplate: true }
                                    if (category) {
                                        // Find or create the section within the category
                                        let section = category.sectionRefs.find(sec => sec.sectionId === linkedNS.sectionRef?.id);

                                        if (section) {
                                            //section exists on template, add this nonStandard to it
                                            section.nonstandards = [...section.nonstandards, { id: clonedNS.id, name: template.name }];
                                        } else {
                                            // Section doesn't exist, create it and add the non-standard
                                            const newSectionRef = {
                                                sectionId: linkedNS.sectionRef?.id || '',
                                                nonstandards: [{ id: clonedNS.id, name: template.name }]
                                            };
                                            category.sectionRefs = [...(category.sectionRefs || []), newSectionRef];
                                        }
                                    } else {
                                        // Category doesn't exist, create a new category with the section and non-standard
                                        let categoryId = props.benefit?.id ?? props.groupConfiguration?.id;
                                        const newCategory = {
                                            categoryID: categoryId ?? "",
                                            categoryType: linkedNSCategoryType?.bcbs_name ?? "",
                                            sectionRefs: [{
                                                sectionId: linkedNS.sectionRef?.id || '',
                                                nonstandards: [{ id: clonedNS.id, name: template.name }]
                                            }]
                                        };
                                        newRelationshipList = [...newRelationshipList, newCategory];
                                    }
                                    
                                    //need to add the new nonstandard to every relationship other than the one we just added to
                                    newRelationshipList.forEach(cat => {
                                        if (cat.categoryID !== incomingNSCategoryId) {
                                            //send the linkedNS to matching. determine if there is a match and return a NONStandard then do conditional stuff
                                            let matchingNonStandardIds = matchingNonStandard(linkedNS, template, cat);

                                            //if we have ids in the list, we can skip this creation of new ones
                                            if (matchingNonStandardIds && matchingNonStandardIds.length > 0) {
                                                matchingNonStandardIds.forEach(id => {
                                                    let benefit = props.state.data.json.benefits.find(q => q.id === cat.categoryID);
                                                    //get the nonstandard from the GC or Benefit
                                                    let categoryNonStandard = benefit ? benefit.nonStandards.find(q => q.id === id) : props.state.data.json.nonStandards.find(q => q.id === id);
                                                    if (categoryNonStandard)
                                                    {
                                                        //update the matched nonstandards for this category
                                                        const updatedNonStandard: FAM.INonStandard = structuredClone(categoryNonStandard);
                                                        updatedNonStandard.partOfTemplate = true;
                                                        updatedNonStandard.name = categoryNonStandard.name;

                                                        let nonStandardBenefitDefintion = updatedNonStandard.fields.find(field => field.configRef.id === "ff72f3f2-d38d-ec11-b400-0022480a9a56");
                                                        let nonStandardPayAccordingToBenefit = updatedNonStandard.fields.find(field => field.configRef.id === "fd72f3f2-d38d-ec11-b400-0022480a9a56");

                                                        let templateDefintion = fieldMap["1fe66c74-710c-ef11-9f89-6045bdeb64f8"];

                                                        const benefitDefinitionValue = templateDefintion.values[0].value; //template value
                                                        nonStandardBenefitDefintion!.values[0].value = structuredClone(benefitDefinitionValue); //ns value

                                                        if (nonStandardPayAccordingToBenefit)
                                                        {
                                                            let templatePayAccordingToBenefit = fieldMap["05beb613-720c-ef11-9f89-6045bdeb64f8"];
                                                            const payAccordingToBenefitValue = templatePayAccordingToBenefit.values[0].value; //template value
                                                            nonStandardPayAccordingToBenefit!.values[0].value = structuredClone(payAccordingToBenefitValue); //ns value
                                                        }

                                                        // Find or create the section within the category
                                                        let section = cat.sectionRefs.find(sec => sec.sectionId === linkedNS.sectionRef?.id);

                                                        if (section) {
                                                            // Section exists, add the non-standard to it

                                                            section.nonstandards = [...section.nonstandards, { id: updatedNonStandard.id, name: updatedNonStandard.name }];
                                                        } else {
                                                            // Section doesn't exist, create it and add the non-standard
                                                            const newSectionRef = {
                                                                sectionId: linkedNS.sectionRef?.id || '',
                                                                nonstandards: [{ id: updatedNonStandard.id, name: updatedNonStandard.name }]
                                                            };
                                                            cat.sectionRefs = [...(cat.sectionRefs || []), newSectionRef];
                                                        }

                                                        dispatch(addUpdateNonStandards([{
                                                            config: props.state.data.config,
                                                            nonStandards: [updatedNonStandard],
                                                            benefit: benefit,
                                                            groupConfiguration: props.groupConfiguration,
                                                            changeTracker: {}
                                                        }]));
                                                    }
                                                })

                                            } else {
                                                let benefit = props.state.data.json.benefits.find(ben => (ben.id === cat.categoryID));
                                                const newNonStandard: FAM.INonStandard = createNonStandard(
                                                    props.state.data,
                                                    {
                                                        section: linkedNSSection!,
                                                        benefit: benefit,
                                                        groupConfiguration: props.groupConfiguration,
                                                        templated: true,
                                                        name: pick.item.nonStandardTemplate.name,
                                                    }
                                                );

                                                //this newly created one needs to have the fields from tempalte - for some reason these ones arent caught in the wide update
                                                let nonStandardBenefitDefintion = newNonStandard.fields.find(field => field.configRef.id === "ff72f3f2-d38d-ec11-b400-0022480a9a56");
                                                let nonStandardPayAccordingToBenefit = newNonStandard.fields.find(field => field.configRef.id === "fd72f3f2-d38d-ec11-b400-0022480a9a56");
                                                let templateDefintion = fieldMap["1fe66c74-710c-ef11-9f89-6045bdeb64f8"];

                                                const benefitDefinitionValue = templateDefintion!.values[0].value; //template value
                                                nonStandardBenefitDefintion!.values[0].value = structuredClone(benefitDefinitionValue); //ns value

                                                if (nonStandardPayAccordingToBenefit) {
                                                    let templatePayAccordingToBenefit = fieldMap["05beb613-720c-ef11-9f89-6045bdeb64f8"];
                                                    const payAccordingToBenefitValue = templatePayAccordingToBenefit.values[0].value; //template value
                                                    nonStandardPayAccordingToBenefit!.values[0].value = structuredClone(payAccordingToBenefitValue); //ns value
                                                }


                                                dispatch(addUpdateNonStandards([{
                                                    config: props.state.data.config,
                                                    nonStandards: [newNonStandard],
                                                    benefit: benefit,
                                                    //groupConfiguration: props.groupConfiguration,
                                                    changeTracker: {}
                                                }]));
                                                // Find or create the section within the category
                                                let section = cat.sectionRefs.find(sec => sec.sectionId === linkedNS.sectionRef?.id);

                                                if (section) {
                                                    // Section exists, add the non-standard to it

                                                    section.nonstandards = [...section.nonstandards, { id: newNonStandard.id, name: newNonStandard.name }];
                                                } else {
                                                    // Section doesn't exist, create it and add the non-standard
                                                    const newSectionRef = {
                                                        sectionId: linkedNS.sectionRef?.id || '',
                                                        nonstandards: [{ id: newNonStandard.id, name: newNonStandard.name }]
                                                    };
                                                    cat.sectionRefs = [...(cat.sectionRefs || []), newSectionRef];
                                                }
                                            }
                                        }
                                    });
                                    // update the intial NS to be considered Templated and update the fields.
                                    const updatedNonStandard: FAM.INonStandard = structuredClone(linkedNS);
                                    updatedNonStandard.partOfTemplate = true;
                                    updatedNonStandard.name = template.name;

                                    
                                    dispatch(addUpdateNonStandards([{
                                        config: props.state.data.config,
                                        nonStandards: [updatedNonStandard],
                                        benefit: props.benefit,
                                        groupConfiguration: props.groupConfiguration,
                                        changeTracker: {}
                                    }]));
                                }); 
                            }

                            template.fields.forEach(templateField => {

                                const field = fieldMap[templateField.configRef.id];

                                // did we find a matching field
                                if (field) {
                                    if (field.values.length === 0) {
                                        // nop
                                    }
                                    else if (field.values.length === 1 && templateField.values.length === 1) {

                                        const value = field.values[0].value;
                                        templateField.values[0].value = structuredClone(value);

                                    } else if (field.values.length > 1) {
                                        let predicate = (oldValue: FAM.IFlexibleFieldValue, newValue: FAM.IFlexibleFieldValue) => {
                                            return oldValue.type === newValue.type;
                                        }
                                        if (tierChanges.length > 0) {
                                            predicate = (oldValue: FAM.IFlexibleFieldValue, newValue: FAM.IFlexibleFieldValue) => {
                                                return !!tierChanges.find(q => q.oldTier?.id === oldValue.tierRef?.id && q.newTier?.id === newValue.tierRef?.id)
                                            };
                                        }

                                        templateField.values.forEach(t => {

                                            const value = field.values.find(q => predicate(q, t));

                                            if (value?.value) {
                                                t.value = structuredClone(value.value);
                                            }
                                        });
                                    }
                                }
                            });

                            const updatedTemplate = {
                                ...template,
                                relationships: newRelationshipList
                            };
                            nonStandardTemplates.push(updatedTemplate);
                        });

                        if (nonStandardTemplates && nonStandardTemplates.length > 0) {

                            dispatch(
                                addUpdateNonStandardTemplates([
                                    {
                                        nonStandardTemplates: nonStandardTemplates
                                    }
                                ])
                            );
                            updateAllTemplatedNonStandardsFields(nonStandardTemplates[0]);
                            setAlertIsOpen(true);
                        }

                        //props.onDismiss?.();
                    }}
                    onCancel={() => {
                        setSourceContext(q => {
                            return {
                                ...q,
                                nonStandardSearchResult: null,
                                scopeSearchResult: null,
                            }
                        });
                    }}
                    onDismiss={() => {
                        setSourceContext(q => {
                            return {
                                ...q,
                                nonStandardSearchResult: null,
                                scopeSearchResult: null,
                            }
                        });
                    }}
                />

            </Stack>
        </div>
    }


    function matchingNonStandard(incomingNonStandard: FAM.INonStandard, incomingTemplate: FAM.INonStandardTemplate, categoryToCheck: FAM.ITemplateRelationship)
    {
        let returnIds: FAM.TGuid[] = [];
        const nonStandard = structuredClone(incomingNonStandard);
        const template = structuredClone(incomingTemplate);
        const templateFieldMap = ArrayToLookupByPredicate(template.fields, q => q.configRef.id);
        let incomingNSCategoryId = categoryToCheck.categoryID;
        const nonStandardFields = ArrayToLookupByPredicateWithMap(nonStandard!.fields, t => t.configRef.id, t => {
            const attribute = props.state.data.config.cache.map_bcbs_config_attribute[t.configRef.id];
            const masterAttribute = props.state.data.config.cache.map_bcbs_config_masterattribute[attribute._bcbs_config_masterattributeid_value!];
            const controlType = masterAttribute.bcbs_attributetypecode as EControlType;
            return {
                field: t,
                attribute,

                masterAttribute,
                controlType,
            }
        });
        const nonStandardDefinition = nonStandardFields['ff72f3f2-d38d-ec11-b400-0022480a9a56'];
        let nonStandardDefinitionValue = "";

        if (nonStandardDefinition.field.values[0].value !== null) {
            nonStandardDefinitionValue = friendlyValue(nonStandardDefinition.controlType, nonStandardDefinition.field.values[0].value);
        }

        let templateDefintion = templateFieldMap["1fe66c74-710c-ef11-9f89-6045bdeb64f8"];

        let templateDefinitionValue = friendlyValue(nonStandardDefinition.controlType, templateDefintion.values[0].value);
        let formattedTemplateDefinitionValue = templateDefinitionValue.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
        let formattedNonStandardDefinition = nonStandardDefinitionValue.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

        //take incoming category id

        //only find section refs of section ref on ns
        categoryToCheck.sectionRefs?.forEach(section => {
            if (section.sectionId === nonStandard.sectionRef?.id)
            {
                let category: FAM.IBenefit | FAM.IGroupConfigurationData;
                //check group config id
                if (props.state.data.json.id === incomingNSCategoryId)
                {
                    category = props.state.data.json;
                    //go through each category non standard, make sure section ref is equal to sectionID, then compare
                    category.nonStandards.forEach(nonStandard => {
                        if (nonStandard.sectionRef?.id === section.sectionId && nonStandard.deactivated !== true) {
                            //we are in the section
                            //get non standard definition
                            const currentNonStandardFieldMap = ArrayToLookupByPredicate(nonStandard.fields, q => q.configRef.id);
                            const currentNonStandardDefinition = currentNonStandardFieldMap['ff72f3f2-d38d-ec11-b400-0022480a9a56'];
                            let currentNonStandardDefinitionValue = friendlyValue(nonStandardDefinition.controlType, currentNonStandardDefinition.values[0].value);
                            let formattedCurrentNonStandardDefinitionValue = currentNonStandardDefinitionValue.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

                            //compare they are the same
                            if (formattedNonStandardDefinition === formattedCurrentNonStandardDefinitionValue) {
                                //add id to list if they are the same
                                returnIds.push(nonStandard.id);
                            }
                        }
                    })
                }
                //check benefitID's
                let benefit = props.state.data.json.benefits.find(benefit => benefit.id === incomingNSCategoryId);
                if (benefit)
                {
                    category = benefit;
                    //go through each category non standard, make sure section ref is equal to sectionID, then compare
                    category.nonStandards.forEach(nonStandard => {
                        if (nonStandard.sectionRef?.id === section.sectionId && nonStandard.deactivated !== true)
                        {
                            //we are in the section
                            //get non standard definition
                            const currentNonStandardFieldMap = ArrayToLookupByPredicate(nonStandard.fields, q => q.configRef.id);
                            const currentNonStandardDefinition = currentNonStandardFieldMap['ff72f3f2-d38d-ec11-b400-0022480a9a56'];
                            let currentNonStandardDefinitionValue = friendlyValue(nonStandardDefinition.controlType, currentNonStandardDefinition.values[0].value);
                            let formattedCurrentNonStandardDefinitionValue = currentNonStandardDefinitionValue.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

                            //compare they are the same
                            if (formattedNonStandardDefinition === formattedCurrentNonStandardDefinitionValue)
                            {
                                //add id to list if they are the same
                                returnIds.push(nonStandard.id);
                            }
                        }
                    })
                }
            }
        })

        return returnIds;
    }

    function updateAllTemplatedNonStandardsFields(incomingTemplate: FAM.INonStandardTemplate) {
        //read template fields
        //clone the template
        const template = structuredClone(incomingTemplate);
        const templateFields = ArrayToLookupByPredicateWithMap(template!.fields, t => t.configRef.id, t => {
            const attribute = props.state.data.config.cache.map_bcbs_config_attribute[t.configRef.id];
            const masterAttribute = props.state.data.config.cache.map_bcbs_config_masterattribute[attribute._bcbs_config_masterattributeid_value!];
            const controlType = masterAttribute.bcbs_attributetypecode as EControlType;
            return {
                field: t,
                attribute,

                masterAttribute,
                controlType,
            }
        });
        const templateDefinition = templateFields['1fe66c74-710c-ef11-9f89-6045bdeb64f8'];
        const templatePayAccordingToBenefit = templateFields['05beb613-720c-ef11-9f89-6045bdeb64f8'];
        let templateDefinitionValue = "";
        let templatePayAccordingToBenefitValue = "";

        if (templateDefinition.field.values[0].value !== null) {
            templateDefinitionValue = friendlyValue(templateDefinition.controlType, templateDefinition.field.values[0].value);
        }
        if (templatePayAccordingToBenefit.field.values[0].value !== null) {
            templatePayAccordingToBenefitValue = friendlyValue(templatePayAccordingToBenefit.controlType, templatePayAccordingToBenefit.field.values[0].value);
        }

        //go through each connected NS and update the fields on that.
        template?.relationships?.forEach(rel => {
            let categoryId = rel.categoryID;
            rel.sectionRefs?.forEach(sectionRef => {
                let sectionId = sectionRef.sectionId;
                sectionRef.nonstandards?.forEach(nonStandardRef => {
                    //find the benefits for the category id
                    let benefit = props.state.data.json.benefits.find(ben => ben.id === categoryId);
                    if (benefit) {
                        //do a find on the non standard
                        let benefitNonStandard = benefit.nonStandards.find(ns => ns.id === nonStandardRef.id);

                        //if we find the non standard, update the fields
                        if (benefitNonStandard && benefitNonStandard.deactivated !== true) {
                            //update fields
                            const updatedNonStandard: FAM.INonStandard = structuredClone(benefitNonStandard);
                            updatedNonStandard.partOfTemplate = true;
                            updatedNonStandard.name = template.name;

                            let nonStandardBenefitDefintion = updatedNonStandard.fields.find(field => field.configRef.id === "ff72f3f2-d38d-ec11-b400-0022480a9a56");
                            let nonStandardPayAccordingToBenefit = updatedNonStandard.fields.find(field => field.configRef.id === "fd72f3f2-d38d-ec11-b400-0022480a9a56");

                            const benefitDefinitionValue = templateDefinition.field.values[0].value; //template value
                            nonStandardBenefitDefintion!.values[0].value = structuredClone(benefitDefinitionValue); //ns value

                            if (nonStandardPayAccordingToBenefit) {
                                const payAccordingToBenefitValue = templatePayAccordingToBenefit.field.values[0].value; //template value
                                nonStandardPayAccordingToBenefit!.values[0].value = structuredClone(payAccordingToBenefitValue); //ns value
                            }

                            dispatch(addUpdateNonStandards([{
                                config: props.state.data.config,
                                nonStandards: [updatedNonStandard],
                                benefit: benefit,
                                //groupConfiguration: props.groupConfiguration,
                                changeTracker: {}
                            }]));
                        }
                    } else if (props.state.data.json.id === categoryId) {
                        //if no benefits look at the GC
                        let gcNonStandard = props.state.data.json.nonStandards.find(ns => ns.id === nonStandardRef.id);
                        if (gcNonStandard && gcNonStandard.deactivated !== true) {
                            //update fields
                            const updatedNonStandard: FAM.INonStandard = structuredClone(gcNonStandard);
                            updatedNonStandard.name = template.name;
                            updatedNonStandard.partOfTemplate = true;

                            let nonStandardBenefitDefintion = updatedNonStandard.fields.find(field => field.configRef.id === "ff72f3f2-d38d-ec11-b400-0022480a9a56");
                            let nonStandardPayAccordingToBenefit = updatedNonStandard.fields.find(field => field.configRef.id === "fd72f3f2-d38d-ec11-b400-0022480a9a56");

                            const benefitDefinitionValue = templateDefinition.field.values[0].value; //template value
                            nonStandardBenefitDefintion!.values[0].value = structuredClone(benefitDefinitionValue); //ns value

                            if (nonStandardPayAccordingToBenefit) {
                                const payAccordingToBenefitValue = templatePayAccordingToBenefit.field.values[0].value; //template value
                                nonStandardPayAccordingToBenefit!.values[0].value = structuredClone(payAccordingToBenefitValue); //ns value
                            }

                            dispatch(addUpdateNonStandards([{
                                config: props.state.data.config,
                                nonStandards: [updatedNonStandard],
                                benefit: benefit,
                                groupConfiguration: props.state.data.json,
                                changeTracker: {}
                            }]));
                        }
                    }
                })
            })
        })
    }

    const create = () =>
    {
        let createdTemplate = structuredClone(_nonStandardTemplate);
        if (createdTemplate)
        {
            createdTemplate!.linkedCategoryTypeId = linkedNSCategoryType?.bcbs_config_categoryid ?? undefined;
            createdTemplate!.linkedCategoryName = linkedNSCategoryType?.bcbs_name ?? undefined;
            let relationshipExists = false;
            let categoryId = props.benefit?.id ?? props.groupConfiguration?.id;

            createdTemplate.relationships?.forEach(relationship => {
                if (relationship.categoryID === categoryId)
                    relationshipExists = true;
            });

            if (!relationshipExists) {
                //this is for creating a new template
                //create new relationship 
                if (linkedNSSection) {
                    let sectionRef: FAM.ITemplateSectionRelationship =
                    {
                        sectionId: linkedNSSection.bcbs_config_sectionid ?? "",
                        nonstandards: [{
                            id: props.nonStandard?.id ?? "",
                            name: props.nonStandard?.name ?? "",
                        }],

                    };
                    createdTemplate.relationships?.push(
                        {
                            categoryID: categoryId ?? "",
                            categoryType: linkedNSCategoryType?.bcbs_name ?? "",
                            sectionRefs: [sectionRef],
                        });
                }
            }
        }

        if (createdTemplate !== null) {
            dispatch(addUpdateNonStandardTemplates([{
                nonStandardTemplates: [createdTemplate],
            }]));

            //update the non standard - if we linked one, to be templated
            if (linkedNSSection && props.nonStandard) {

                //update the nonstandard for this to be with the template we just made
                const updatedNonStandard: FAM.INonStandard = structuredClone(props.nonStandard);
                updatedNonStandard.partOfTemplate = true;
                const fieldMap = ArrayToLookupByPredicate(createdTemplate.fields, q => q.configRef.id);
                let nonStandardBenefitDefintion = updatedNonStandard.fields.find(field => field.configRef.id === "ff72f3f2-d38d-ec11-b400-0022480a9a56");
                let nonStandardPayAccordingToBenefit = updatedNonStandard.fields.find(field => field.configRef.id === "fd72f3f2-d38d-ec11-b400-0022480a9a56");

                let templateDefintion = fieldMap["1fe66c74-710c-ef11-9f89-6045bdeb64f8"];

                const benefitDefinitionValue = templateDefintion.values[0].value; //template value
                nonStandardBenefitDefintion!.values[0].value = structuredClone(benefitDefinitionValue); //ns value

                if (nonStandardPayAccordingToBenefit) {
                    let templatePayAccordingToBenefit = fieldMap["05beb613-720c-ef11-9f89-6045bdeb64f8"];
                    const payAccordingToBenefitValue = templatePayAccordingToBenefit.values[0].value; //template value
                    nonStandardPayAccordingToBenefit!.values[0].value = structuredClone(payAccordingToBenefitValue); //ns value
                }
                

                dispatch(addUpdateNonStandards([{
                    config: props.state.data.config,
                    nonStandards: [updatedNonStandard],
                    benefit: props.benefit,
                    groupConfiguration: props.groupConfiguration,
                    changeTracker: {}
                }]));
            }

            props.onDismiss?.();
        }
    }

    function createHeaderText(): string | undefined {
        if (props.nonStandard)
        {
            return `Create New Template To Link To ${props.nonStandard.name}`;
        }
        return "Non-Standard Template";
    }

    function onRenderSource(): JSX.Element {
        return <div className={fadeIn}>
            <ChoiceGroup
                options={optionsSource}
                label="Choose Source"
                required={true}
                selectedKey={optionSource?.key}
                onChange={(ev, option?: IValueChoiceGroupOption<EView>) => {
                    setOptionSource(option ?? null);
                }}
            />
        </div >;
    }

    const onRenderValidationDialog = () => {
        if (!alertIsMounted)
            return <></>;

        return <ConfirmationDialog
            onContinue={() => {
                (async () => {
                    try {
                        dispatch(setProcessing(true));
                        const excelExtractImport = await import("global/ExcelExtract")
                        const { NonstandardExcelGeneration } = excelExtractImport;
                        const result = await NonstandardExcelGeneration(props.state, dataSource)
                        const fileName = `NSE-${props.state.data.snapshot?.bcbs_name}(${props.state.data.snapshot?.bcbs_groupconfigurationsnapshotid})-${new Date().getTime()}.xlsx`;
                        saveAs(result.file, fileName);
                    } catch (error) {
                        emitCustomEvent({
                            eventType: 'globalException',
                            details: error
                        });
                    } finally {
                        dispatch(setProcessing(false));
                    }
                })();
                setAlertIsOpen(false); setAlertIsMounted(false); props.onDismiss?.();
            }}
            onClose={() => { setAlertIsOpen(false); setAlertIsMounted(false); props.onDismiss?.(); }}
            continueText="Open NSE"
            cancelText="Close"
        >
            <Text>
            <p>Non-Standards have been updated or added as a result of this template action. </p>
            <p>You can open then NSE extract to view all Non-Standards and their changes.</p>
            </Text>
        </ConfirmationDialog>;
    }

    function onRenderEntryFooter(): JSX.Element {
        return <div className={fadeIn}>
            <PrimaryButton text='Continue'
                styles={panelButtonsStyles}
                disabled={optionSource === null}
                onClick={async () => {

                    const view = optionSource?.data;

                    if (view === EView.View_LinkTemplate) {

                        setSourceContext({
                            snapshot: props.state.data.snapshot,
                            json: props.state.data.json,
                            scopeSearchResult: null,
                        });

                    }

                    setView(optionSource?.data ?? null);
                }}
            />
            <DefaultButton text='Cancel'
                onClick={() => props.onCancel?.()}
                styles={panelButtonsStyles}
            />
        </div>
    }

    function onRenderCopyFooter(): JSX.Element {
        return <div className={fadeIn}>
            <PrimaryButton text='Create'
                styles={panelButtonsStyles}
                onClick={create}
            />
            <DefaultButton text='Cancel'
                styles={panelButtonsStyles}
                onClick={() => setView(EView.View_Source)}
            />
        </div>;
    }

    function onRenderCategoryType(): JSX.Element
    {
        if (!linkedNSCategoryType) {
            return <></>
        }

        return <TextFieldControl
            disabled={true}
            label={"Category Type"}
            defaultFieldValue={{ type: 'string', value: linkedNSCategoryType?.bcbs_name ?? '' }}
                />
    }

    function onRenderAdd(): JSX.Element {

        return <div className={fadeIn}>
            {onRenderCategoryType()}
            <FlexibleFieldView
            fields={_nonStandardTemplate?.fields!}
            tiers={[]}
            scopeType={EScopeType.nonStandardTemplate}
            category={categoryNonStandardTemplate}
            disabled={false}
            lastPivotItem={[onRenderProperties]}
                onChange={(payload) => {
                setNonStandardTemplate(xNonStandardTemplate => {
                    const nNonStandardTemplate = { ...xNonStandardTemplate! };

                    nNonStandardTemplate!.linkedCategoryTypeId = linkedNSCategoryType?.bcbs_config_categoryid ?? undefined;
                    nNonStandardTemplate!.linkedCategoryName = linkedNSCategoryType?.bcbs_name ?? undefined;
                    
                    let relationshipExists = false;
                    let categoryId = props.benefit?.id ?? props.groupConfiguration?.id;

                    nNonStandardTemplate.relationships?.forEach(relationship => {
                        if (relationship.categoryID === categoryId)
                            relationshipExists = true;
                    });

                    if (!relationshipExists) { 
                        //this is for creating a new template
                        //create new relationship 
                        if (linkedNSSection) {
                            let sectionRef: FAM.ITemplateSectionRelationship =
                            {
                                sectionId: linkedNSSection.bcbs_config_sectionid ?? "",
                                nonstandards: [{
                                    id: props.nonStandard?.id ?? "",
                                    name: props.nonStandard?.name ?? "",
                                }],
                                    
                            };
                            nNonStandardTemplate.relationships?.push(
                            {
                                categoryID: categoryId ?? "",
                                categoryType: linkedNSCategoryType?.bcbs_name ?? "",
                                sectionRefs: [sectionRef],
                            });
                        }                        
                    }
                    /*const fields = nNonStandardTemplate.fields;*/
                    const field = nNonStandardTemplate?.fields?.find(q => q.id === payload.field!.id);

                    if (field) {
                        const fieldValue = field?.values?.find(q => q.id === payload.fieldValue!.id);

                        if (fieldValue)
                            fieldValue.value = payload.value!;
                    }

                    return nNonStandardTemplate;
                });
            }}
            />
        </div>;
    }

    function onRenderUnknown(): JSX.Element {
        return <div>Unknown</div>
    }

    const onRenderBody: IRenderFunction<IPanelProps> = (bodyProps, defaultRender) => {
        let onRender = onRenderUnknown;

        switch (view) {
            case EView.View_Source:
                onRender = onRenderSource
                break;
            
            case EView.View_CreateTemplate:
                onRender = onRenderAdd
                break;

            case EView.View_LinkTemplate:
                onRender = onRenderLinkTemplate
                break;

            default:
                onRender = onRenderUnknown
                break;
        }

        return <>
            <Separator />
            {
                validationMessage &&
                <>
                    <MessageBar className={fadeIn} messageBarType={MessageBarType.error} onDismiss={() => setValidationMessage("")}                >
                        {validationMessage}
                    </MessageBar>
                </>
            }
            <div>
                {onRender()}
            </div>
        </>
    }

    const onRenderFooterContent: IRenderFunction<IPanelProps> = (contextProps, defaultRender) => {
        let onRender = onRenderUnknown;

        switch (view) {
            case EView.View_Source:
                onRender = onRenderEntryFooter
                break;

            case EView.View_CreateTemplate:
                onRender = onRenderCopyFooter
                break;

            case EView.View_LinkTemplate:
                onRender = onRenderCopyFooter
                break;

            default:
                onRender = onRenderUnknown
                break;
        }

        return onRender();
    }

    

    return <Panel
        isOpen={props.isOpen}
        onDismiss={props.onCancel}
        type={PanelType.medium}
        closeButtonAriaLabel="Close"
        headerText={createHeaderText()}
        isLightDismiss={true}
        onRenderFooterContent={onRenderFooterContent}
        isFooterAtBottom={true}
    >
        {onRenderBody()}
    </Panel>;

}




function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(NonStandardTemplateWizard);



